import React, { useRef, useCallback, useMemo, useState, useEffect } from 'react';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import { EntrancePosition } from '../context';
import {
    areaBBoxPx,
    computeRoom,
    EditorArea,
    nearestPointOnAreaOutline,
    OutlinePoint,
    Point,
    polygonPointsPx,
    RoomRect,
    SizeMeters,
    snapPointMeters,
    SVG_HEIGHT,
    SVG_WIDTH,
} from '../mapGeometry';

export { SVG_WIDTH, SVG_HEIGHT };

const WALL_STROKE = 6;
const WALL_COLOR = '#9E9E9E';
const MAGENTA = theme.palette.primary.main; // #99567E
const GRID_SIZE_METERS = 1;
const ENTRANCE_GAP = 32; // notch gap in wall (px)
const ENTRANCE_RADIUS = 22; // door arc radius (px)
// SVG canvas background — used to erase the wall at the notch during drag
const CANVAS_BG = '#fafafa';

// Zoom config
export const ZOOM_MIN = 0.5;
export const ZOOM_MAX = 5.0;
export const ZOOM_STEP = 0.25;

interface MapEditorCanvasProps {
    gridEnabled: boolean;
    zoom: number;
    size: SizeMeters;
    area: EditorArea;
    entrance: EntrancePosition | null;
    isMarkingEntrance: boolean;
    /** Fired on a marking-mode click (new entrance) and on a drag commit (reposition). */
    onEntranceChange: (position: EntrancePosition) => void;
    children?: React.ReactNode;
    /** Optional external ref so callers can do their own clientToSVG conversions */
    svgRef?: React.RefObject<SVGSVGElement>;
    /**
     * When true: the entrance drag handle and outline hitbox are rendered so the
     * user can reposition the entrance. Pass only on the Map Configuration step.
     * Defaults to false so placement/details steps render the entrance as static.
     */
    allowEntranceDrag?: boolean;
    /**
     * When true: the global rectangle is stroked as a dashed guide behind the
     * area outline, so offset/size fields measured against it have a visual
     * referent. Pass only on the Map Configuration step.
     */
    showGlobalRectGuide?: boolean;
}

/** Convert a client-space pointer event position to SVG user units */
function clientToSVG(
    svg: SVGSVGElement,
    clientX: number,
    clientY: number
): { x: number; y: number } {
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const inv = ctm.inverse();
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const svgPt = pt.matrixTransform(inv);
    return { x: svgPt.x, y: svgPt.y };
}

function svgToMetres(svgPt: Point, room: RoomRect): Point {
    return { x: (svgPt.x - room.x) / room.pxPerMeter, y: (svgPt.y - room.y) / room.pxPerMeter };
}

function metresToPx(p: Point, room: RoomRect): Point {
    return { x: room.x + p.x * room.pxPerMeter, y: room.y + p.y * room.pxPerMeter };
}

function entranceToMetres(entrance: EntrancePosition, size: SizeMeters): Point {
    return { x: entrance.x * size.width, y: entrance.y * size.height };
}

function metresToEntrance(p: Point, size: SizeMeters): EntrancePosition {
    return { x: p.x / size.width, y: p.y / size.height };
}

/** Renders the area outline as a stroked shape — one wall segment per kind. */
function AreaOutline({
    area,
    room,
    dimmed,
}: {
    area: EditorArea;
    room: RoomRect;
    dimmed: boolean;
}) {
    const stroke = dimmed ? '#ccaabb' : WALL_COLOR;
    if (area.kind === 'Ellipse') {
        const bbox = areaBBoxPx(area, room);
        return (
            <ellipse
                cx={bbox.x + bbox.width / 2}
                cy={bbox.y + bbox.height / 2}
                rx={bbox.width / 2}
                ry={bbox.height / 2}
                fill="none"
                stroke={stroke}
                strokeWidth={WALL_STROKE}
            />
        );
    }
    if (area.kind === 'Polygon') {
        const points = polygonPointsPx(area, room);
        return (
            <polygon
                points={points.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke={stroke}
                strokeWidth={WALL_STROKE}
                strokeLinejoin="round"
            />
        );
    }
    const bbox = areaBBoxPx(area, room);
    return (
        <rect
            x={bbox.x}
            y={bbox.y}
            width={bbox.width}
            height={bbox.height}
            fill="none"
            stroke={stroke}
            strokeWidth={WALL_STROKE}
            strokeLinecap="square"
        />
    );
}

/** Invisible thick-stroke outline used as the click target while marking the entrance. */
function AreaOutlineHitbox({
    area,
    room,
    onPointerDown,
}: {
    area: EditorArea;
    room: RoomRect;
    onPointerDown: (e: React.PointerEvent<SVGElement>) => void;
}) {
    const common = {
        fill: 'none' as const,
        stroke: 'transparent',
        strokeWidth: 24,
        style: { cursor: 'crosshair' },
        onPointerDown,
    };
    if (area.kind === 'Ellipse') {
        const bbox = areaBBoxPx(area, room);
        return (
            <ellipse
                cx={bbox.x + bbox.width / 2}
                cy={bbox.y + bbox.height / 2}
                rx={bbox.width / 2}
                ry={bbox.height / 2}
                {...common}
            />
        );
    }
    if (area.kind === 'Polygon') {
        const points = polygonPointsPx(area, room);
        return <polygon points={points.map((p) => `${p.x},${p.y}`).join(' ')} {...common} />;
    }
    const bbox = areaBBoxPx(area, room);
    return <rect x={bbox.x} y={bbox.y} width={bbox.width} height={bbox.height} {...common} />;
}

// Ellipse has no straight edges to single out, so the accent is a short arc of
// the perimeter centered on the entrance's parametric angle.
const ELLIPSE_ACCENT_HALF_ANGLE = (15 * Math.PI) / 180;

/**
 * Highlights the outline segment that holds the entrance — the whole side for
 * Rectangle, the whole containing edge for Polygon, or a ±15° arc around the
 * entrance's parametric angle for Ellipse. Rendered OUTSIDE the drag-translated
 * group so it stays fixed while only the marker moves during a live drag.
 */
function EntranceEdgeHighlight({
    outline,
    area,
    room,
    isMarkingEntrance,
}: {
    outline: OutlinePoint;
    area: EditorArea;
    room: RoomRect;
    isMarkingEntrance: boolean;
}) {
    const stroke = isMarkingEntrance ? '#ccaabb' : MAGENTA;
    if (!outline.edge) {
        const bbox = areaBBoxPx(area, room);
        const cx = bbox.x + bbox.width / 2;
        const cy = bbox.y + bbox.height / 2;
        const rx = bbox.width / 2;
        const ry = bbox.height / 2;
        const entrancePx = metresToPx(outline.point, room);
        const angle = Math.atan2((entrancePx.y - cy) / ry, (entrancePx.x - cx) / rx);
        const from = {
            x: cx + rx * Math.cos(angle - ELLIPSE_ACCENT_HALF_ANGLE),
            y: cy + ry * Math.sin(angle - ELLIPSE_ACCENT_HALF_ANGLE),
        };
        const to = {
            x: cx + rx * Math.cos(angle + ELLIPSE_ACCENT_HALF_ANGLE),
            y: cy + ry * Math.sin(angle + ELLIPSE_ACCENT_HALF_ANGLE),
        };
        return (
            <path
                d={`M ${from.x} ${from.y} A ${rx} ${ry} 0 0 1 ${to.x} ${to.y}`}
                fill="none"
                stroke={stroke}
                strokeWidth={WALL_STROKE}
                strokeLinecap="round"
            />
        );
    }
    const a = metresToPx(outline.edge.a, room);
    const b = metresToPx(outline.edge.b, room);
    return (
        <line
            x1={a.x}
            y1={a.y}
            x2={b.x}
            y2={b.y}
            stroke={stroke}
            strokeWidth={WALL_STROKE}
            strokeLinecap="square"
        />
    );
}

/**
 * Renders the entrance marker at a given px point/normal/tangent: notch (gap in
 * wall), door-jamb ticks, crosshair, and label pill. Works for any outline
 * direction (rect side, ellipse tangent, polygon edge) via normal/tangent
 * rather than a fixed wall enum.
 */
const EntranceMarker = React.memo(function EntranceMarker({
    pointPx,
    normal,
    tangent,
}: {
    pointPx: Point;
    normal: Point;
    tangent: Point;
}) {
    const half = ENTRANCE_GAP / 2;
    const n1 = { x: pointPx.x - tangent.x * half, y: pointPx.y - tangent.y * half };
    const n2 = { x: pointPx.x + tangent.x * half, y: pointPx.y + tangent.y * half };

    const TICK = 8;
    const t1 = { x: n1.x - normal.x * TICK, y: n1.y - normal.y * TICK };
    const t2 = { x: n2.x - normal.x * TICK, y: n2.y - normal.y * TICK };

    const labelOffset = 26;
    const labelX = pointPx.x + normal.x * labelOffset;
    const labelY = pointPx.y + normal.y * labelOffset;

    const ch = 7;
    const notchAngleDeg = (Math.atan2(tangent.y, tangent.x) * 180) / Math.PI;

    // Door arc: quarter-circle hinged at n1, swinging outward along the normal.
    const arcEnd = { x: n1.x + normal.x * ENTRANCE_RADIUS, y: n1.y + normal.y * ENTRANCE_RADIUS };

    return (
        <g>
            {/* Erase the wall at the notch position */}
            <rect
                x={-half}
                y={-WALL_STROKE / 2}
                width={ENTRANCE_GAP}
                height={WALL_STROKE}
                fill={CANVAS_BG}
                transform={`translate(${pointPx.x}, ${pointPx.y}) rotate(${notchAngleDeg})`}
            />

            {/* Door arc: quarter-circle from n1 hinge sweeping OUTWARD */}
            <path
                d={`M ${n1.x} ${n1.y} A ${ENTRANCE_RADIUS} ${ENTRANCE_RADIUS} 0 0 0 ${arcEnd.x} ${arcEnd.y}`}
                stroke={MAGENTA}
                strokeWidth={1.5}
                fill="none"
                strokeLinecap="round"
                strokeDasharray="4 3"
            />

            {/* Door leaf line from hinge to arc end */}
            <line
                x1={n1.x}
                y1={n1.y}
                x2={arcEnd.x}
                y2={arcEnd.y}
                stroke={MAGENTA}
                strokeWidth={1.5}
                strokeLinecap="round"
            />

            {/* Door-jamb ticks at gap edges */}
            <line
                x1={n1.x}
                y1={n1.y}
                x2={t1.x}
                y2={t1.y}
                stroke={MAGENTA}
                strokeWidth={2.5}
                strokeLinecap="round"
            />
            <line
                x1={n2.x}
                y1={n2.y}
                x2={t2.x}
                y2={t2.y}
                stroke={MAGENTA}
                strokeWidth={2.5}
                strokeLinecap="round"
            />

            {/* Crosshair at entrance center */}
            <line
                x1={pointPx.x - ch}
                y1={pointPx.y}
                x2={pointPx.x + ch}
                y2={pointPx.y}
                stroke={MAGENTA}
                strokeWidth={2}
            />
            <line
                x1={pointPx.x}
                y1={pointPx.y - ch}
                x2={pointPx.x}
                y2={pointPx.y + ch}
                stroke={MAGENTA}
                strokeWidth={2}
            />

            {/* Label pill — OUTSIDE the wall */}
            <rect x={labelX - 34} y={labelY - 11} width={68} height={22} rx={11} fill={MAGENTA} />
            <text
                x={labelX}
                y={labelY + 4}
                textAnchor="middle"
                fontSize={11}
                fontWeight={600}
                fill="white"
                fontFamily="-apple-system, BlinkMacSystemFont, Roboto, Segoe UI, sans-serif"
            >
                {i18n.t('tableMaps.wizard.entrance')}
            </text>
        </g>
    );
});

// Minimum SVG units of room that must remain visible after panning
const PAN_MIN_VISIBLE = 80;
// Pointer movement (client px) below which a pointerdown→up is treated as a click
const PAN_CLICK_THRESHOLD = 3;

/**
 * Clamp a pan offset so at least PAN_MIN_VISIBLE SVG units of the room stay
 * inside the viewport. When zoom <= 1 the entire map fits, so pan is always 0.
 */
function clampPan(
    px: number,
    py: number,
    currentZoom: number,
    room: RoomRect
): { x: number; y: number } {
    if (currentZoom <= 1) return { x: 0, y: 0 };
    const vbW = SVG_WIDTH / currentZoom;
    const vbH = SVG_HEIGHT / currentZoom;
    const vbX0 = (SVG_WIDTH - vbW) / 2;
    const vbY0 = (SVG_HEIGHT - vbH) / 2;
    // viewBox left = vbX0 - panX, right = vbX0 - panX + vbW
    // Ensure at least PAN_MIN_VISIBLE of the room is visible
    const maxPanX = vbX0 + vbW - room.x - PAN_MIN_VISIBLE;
    const minPanX = vbX0 - (room.x + room.width) + PAN_MIN_VISIBLE;
    const maxPanY = vbY0 + vbH - room.y - PAN_MIN_VISIBLE;
    const minPanY = vbY0 - (room.y + room.height) + PAN_MIN_VISIBLE;
    return {
        x: Math.min(maxPanX, Math.max(minPanX, px)),
        y: Math.min(maxPanY, Math.max(minPanY, py)),
    };
}

function MapEditorCanvas({
    gridEnabled,
    zoom,
    size,
    area,
    entrance,
    isMarkingEntrance,
    onEntranceChange,
    children,
    svgRef: externalSvgRef,
    allowEntranceDrag = false,
    showGlobalRectGuide = false,
}: MapEditorCanvasProps) {
    const internalSvgRef = useRef<SVGSVGElement>(null);
    const svgRef = externalSvgRef ?? internalSvgRef;

    const room = useMemo(() => computeRoom(size), [size.width, size.height]);

    // Ref to the entrance drag handle element for imperative transform updates
    const entranceDragRef = useRef<SVGCircleElement>(null);
    // Ref to the <g> wrapping EntranceMarker — translated imperatively during drag
    const entranceGRef = useRef<SVGGElement>(null);
    // Track drag state in a ref — no re-render on every pointermove
    const entranceDragActive = useRef(false);

    // Pan offset in SVG user units.
    // Refs are updated imperatively on every pointermove (no re-render per frame).
    // State is committed once on pointerup to trigger a React re-render.
    const panXRef = useRef(0);
    const panYRef = useRef(0);
    const [panState, setPanState] = useState({ x: 0, y: 0 });

    // Pan drag tracking refs
    const panDragActive = useRef(false);
    const panDidMove = useRef(false);
    const panStartClient = useRef({ x: 0, y: 0 });
    const panStartPan = useRef({ x: 0, y: 0 });

    // Reset / re-clamp pan whenever zoom or the global size changes
    useEffect(() => {
        const clamped = clampPan(panXRef.current, panYRef.current, zoom, room);
        panXRef.current = clamped.x;
        panYRef.current = clamped.y;
        setPanState(clamped);
    }, [zoom, room]); // eslint-disable-line react-hooks/exhaustive-deps

    // Effective viewBox dimensions based on zoom (zoom out = larger viewBox)
    const vbWidth = SVG_WIDTH / zoom;
    const vbHeight = SVG_HEIGHT / zoom;
    const vbX = (SVG_WIDTH - vbWidth) / 2;
    const vbY = (SVG_HEIGHT - vbHeight) / 2;
    const viewBox = `${vbX - panState.x} ${vbY - panState.y} ${vbWidth} ${vbHeight}`;

    // Pan handlers — attached to the SVG root. Children that own their own drag
    // (tables, entrance circle) call stopPropagation so pan never starts from them.
    function handleSVGPointerDown(e: React.PointerEvent<SVGSVGElement>) {
        // Only pan when zoomed in and not in entrance-marking mode
        if (zoom <= 1 || isMarkingEntrance || entranceDragActive.current) return;
        panDragActive.current = true;
        panDidMove.current = false;
        panStartClient.current = { x: e.clientX, y: e.clientY };
        panStartPan.current = { x: panXRef.current, y: panYRef.current };
        (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId);
    }

    function handleSVGPointerMove(e: React.PointerEvent<SVGSVGElement>) {
        if (!panDragActive.current) return;
        const dx = e.clientX - panStartClient.current.x;
        const dy = e.clientY - panStartClient.current.y;
        if (!panDidMove.current && Math.hypot(dx, dy) > PAN_CLICK_THRESHOLD) {
            panDidMove.current = true;
        }
        if (!panDidMove.current) return;

        const svgEl = svgRef.current;
        if (!svgEl) return;
        const rect = svgEl.getBoundingClientRect();
        // Convert client pixels → SVG user units (drag right moves content right = panX increases)
        const scaleX = vbWidth / rect.width;
        const scaleY = vbHeight / rect.height;
        const rawX = panStartPan.current.x + dx * scaleX;
        const rawY = panStartPan.current.y + dy * scaleY;
        const clamped = clampPan(rawX, rawY, zoom, room);
        panXRef.current = clamped.x;
        panYRef.current = clamped.y;
        // Imperative update — no setState, no re-render per frame
        svgEl.setAttribute(
            'viewBox',
            `${vbX - clamped.x} ${vbY - clamped.y} ${vbWidth} ${vbHeight}`
        );
        svgEl.style.cursor = 'grabbing';
    }

    function handleSVGPointerUp() {
        if (!panDragActive.current) return;
        panDragActive.current = false;
        if (panDidMove.current) {
            // Commit to state — triggers React re-render which also resets cursor via style prop
            setPanState({ x: panXRef.current, y: panYRef.current });
        }
    }

    function getSVGPoint(clientX: number, clientY: number): { x: number; y: number } {
        if (!svgRef.current) return { x: 0, y: 0 };
        return clientToSVG(svgRef.current, clientX, clientY);
    }

    function handleOutlineClick(e: React.PointerEvent<SVGElement>) {
        if (!isMarkingEntrance) return;
        const svgPt = getSVGPoint(e.clientX, e.clientY);
        const outline = nearestPointOnAreaOutline(svgToMetres(svgPt, room), area);
        onEntranceChange(metresToEntrance(snapPointMeters(outline.point), size));
    }

    // Committed entrance SVG point captured at drag start (avoids stale closures)
    const entranceDragCommittedPx = useRef<Point>({ x: 0, y: 0 });

    // Entrance drag: imperative — no React state changes during pointermove
    const handleEntranceDragStart = useCallback(
        (e: React.PointerEvent<SVGCircleElement>) => {
            if (!entrance) return;
            e.stopPropagation();
            e.currentTarget.setPointerCapture(e.pointerId);
            entranceDragActive.current = true;
            const outline = nearestPointOnAreaOutline(entranceToMetres(entrance, size), area);
            entranceDragCommittedPx.current = metresToPx(outline.point, room);
        },
        [entrance, size, area, room]
    );

    const handleEntranceDragMove = useCallback(
        (e: React.PointerEvent<SVGCircleElement>) => {
            if (!entranceDragActive.current || !svgRef.current) return;
            const svgPt = clientToSVG(svgRef.current, e.clientX, e.clientY);
            const outline = nearestPointOnAreaOutline(svgToMetres(svgPt, room), area);
            const pointPx = metresToPx(snapPointMeters(outline.point), room);

            // Imperatively translate only the marker group — wall highlight stays fixed
            const dx = pointPx.x - entranceDragCommittedPx.current.x;
            const dy = pointPx.y - entranceDragCommittedPx.current.y;
            if (entranceGRef.current) {
                entranceGRef.current.setAttribute('transform', `translate(${dx}, ${dy})`);
            }
            if (entranceDragRef.current) {
                entranceDragRef.current.setAttribute('cx', String(pointPx.x));
                entranceDragRef.current.setAttribute('cy', String(pointPx.y));
            }
        },
        [svgRef, room, area]
    );

    const handleEntranceDragEnd = useCallback(
        (e: React.PointerEvent<SVGCircleElement>) => {
            if (!entranceDragActive.current || !svgRef.current) return;
            entranceDragActive.current = false;
            const svgPt = clientToSVG(svgRef.current, e.clientX, e.clientY);
            const outline = nearestPointOnAreaOutline(svgToMetres(svgPt, room), area);
            // Reset the marker group transform before React re-renders at new position
            if (entranceGRef.current) {
                entranceGRef.current.setAttribute('transform', '');
            }
            onEntranceChange(metresToEntrance(snapPointMeters(outline.point), size));
        },
        [svgRef, room, area, size, onEntranceChange]
    );

    // Full-canvas dot grid — rendered as very first child so it sits behind everything
    const fullCanvasGrid = useMemo(
        () =>
            gridEnabled ? (
                <rect x={0} y={0} width={SVG_WIDTH} height={SVG_HEIGHT} fill="url(#dotGrid)" />
            ) : null,
        [gridEnabled]
    );

    const gridSizePx = GRID_SIZE_METERS * room.pxPerMeter;

    const entranceOutline = entrance
        ? nearestPointOnAreaOutline(entranceToMetres(entrance, size), area)
        : null;
    const entrancePointPx = entranceOutline ? metresToPx(entranceOutline.point, room) : null;

    const svgCursor = isMarkingEntrance ? 'crosshair' : zoom > 1 ? 'grab' : 'default';

    return (
        <svg
            ref={svgRef}
            viewBox={viewBox}
            style={{
                width: '100%',
                height: '100%',
                display: 'block',
                background: CANVAS_BG,
                cursor: svgCursor,
            }}
            onPointerDown={handleSVGPointerDown}
            onPointerMove={handleSVGPointerMove}
            onPointerUp={handleSVGPointerUp}
        >
            <defs>
                {/* Dot grid pattern — anchored on the room origin so 1m cells line up with its walls */}
                <pattern
                    id="dotGrid"
                    x={room.x % gridSizePx}
                    y={room.y % gridSizePx}
                    width={gridSizePx}
                    height={gridSizePx}
                    patternUnits="userSpaceOnUse"
                >
                    <circle cx={gridSizePx / 2} cy={gridSizePx / 2} r={1.5} fill="#c8c8c8" />
                </pattern>
            </defs>

            {/* Full-canvas grid background — behind walls and tables */}
            {fullCanvasGrid}

            {/* Global rectangle guide — the frame offset/size fields are measured against */}
            {showGlobalRectGuide && (
                <rect
                    x={room.x}
                    y={room.y}
                    width={room.width}
                    height={room.height}
                    fill="none"
                    stroke={theme.palette.grey['400']}
                    strokeWidth={1}
                    strokeDasharray="4 4"
                />
            )}

            {/* Area outline (the room's walls) */}
            <AreaOutline area={area} room={room} dimmed={isMarkingEntrance} />

            {/* Entrance wall highlight (full edge, no gap) — stays fixed during drag */}
            {entranceOutline && (
                <EntranceEdgeHighlight
                    outline={entranceOutline}
                    area={area}
                    room={room}
                    isMarkingEntrance={isMarkingEntrance}
                />
            )}

            {/* Entrance marker (notch + door elements) — translated imperatively during drag */}
            <g ref={entranceGRef}>
                {entranceOutline && entrancePointPx && (
                    <EntranceMarker
                        pointPx={entrancePointPx}
                        normal={entranceOutline.normal}
                        tangent={entranceOutline.tangent}
                    />
                )}
            </g>

            {/* Children (table placement layer) */}
            {children}

            {/* Outline hitbox — only in entrance-drag mode so user can click the wall */}
            {allowEntranceDrag && isMarkingEntrance && (
                <AreaOutlineHitbox area={area} room={room} onPointerDown={handleOutlineClick} />
            )}

            {/* Drag handle on the entrance crosshair — only when allowEntranceDrag */}
            {allowEntranceDrag && entrancePointPx && (
                <circle
                    ref={entranceDragRef}
                    cx={entrancePointPx.x}
                    cy={entrancePointPx.y}
                    r={18}
                    fill="transparent"
                    style={{ cursor: 'grab' }}
                    onPointerDown={handleEntranceDragStart}
                    onPointerMove={handleEntranceDragMove}
                    onPointerUp={handleEntranceDragEnd}
                />
            )}
        </svg>
    );
}

export default React.memo(MapEditorCanvas);
